import { createSlice, PayloadAction } from '@reduxjs/toolkit';

// Legacy interface for backward compatibility
export interface SelectedModifier {
  name: string;
  selectedOptions: string[];
  additionalPrice: number;
  isMultiSelect: boolean;
  originalModifier?: any;
}

// New simplified cart item structure
export interface CartItem {
  item: any; // Complete original menu item
  selected: {
    spice: any | null; // Complete spice level object or null
    modifiers: any[]; // Array of selected modifier objects
  };
  totalPrice: number; // Price per item (not including quantity)
  quantity: number;
  cartItemId: string; // Unique identifier for this cart configuration
}

interface CartState {
  items: CartItem[];
  totalItems: number;
  totalAmount: number;
  isOpen: boolean;
}

const initialState: CartState = {
  items: [],
  totalItems: 0,
  totalAmount: 0,
  isOpen: false,
};

const cartSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    addToCart: (state, action: PayloadAction<CartItem>) => {
      const existingItem = state.items.find(item => item.cartItemId === action.payload.cartItemId);
      
      if (existingItem) {
        // Add the new quantity to existing quantity
        existingItem.quantity += (action.payload.quantity || 1);
      } else {
        // Use the quantity from payload, default to 1 if not provided
        state.items.push({ ...action.payload, quantity: action.payload.quantity || 1 });
      }
      
      // Recalculate totals
      state.totalItems = state.items.reduce((total, item) => total + item.quantity, 0);
      state.totalAmount = state.items.reduce((total, item) => total + (item.totalPrice * item.quantity), 0);
    },
    
    removeFromCart: (state, action: PayloadAction<string>) => {
      state.items = state.items.filter(item => item.cartItemId !== action.payload);
      
      // Recalculate totals
      state.totalItems = state.items.reduce((total, item) => total + item.quantity, 0);
      state.totalAmount = state.items.reduce((total, item) => total + (item.totalPrice * item.quantity), 0);
    },
    
    updateQuantity: (state, action: PayloadAction<{ cartItemId: string; quantity: number }>) => {
      const item = state.items.find(item => item.cartItemId === action.payload.cartItemId);
      
      if (item) {
        if (action.payload.quantity <= 0) {
          state.items = state.items.filter(item => item.cartItemId !== action.payload.cartItemId);
        } else {
          item.quantity = action.payload.quantity;
        }
      }
      
      // Recalculate totals
      state.totalItems = state.items.reduce((total, item) => total + item.quantity, 0);
      state.totalAmount = state.items.reduce((total, item) => total + (item.totalPrice * item.quantity), 0);
    },
    
    clearCart: (state) => {
      state.items = [];
      state.totalItems = 0;
      state.totalAmount = 0;
    },
    
    toggleCart: (state) => {
      state.isOpen = !state.isOpen;
    },
    
    openCart: (state) => {
      state.isOpen = true;
    },
    
    closeCart: (state) => {
      state.isOpen = false;
    },
    
    updateCartItem: (state, action: PayloadAction<{ cartItemId: string; updatedItem: Omit<CartItem, 'quantity' | 'cartItemId'> }>) => {
      const existingItemIndex = state.items.findIndex(item => item.cartItemId === action.payload.cartItemId);
      
      if (existingItemIndex !== -1) {
        // Keep the existing quantity and cartItemId, update everything else
        state.items[existingItemIndex] = {
          ...action.payload.updatedItem,
          quantity: state.items[existingItemIndex].quantity,
          cartItemId: action.payload.cartItemId,
        };
        
        // Recalculate totals
        state.totalItems = state.items.reduce((total, item) => total + item.quantity, 0);
        state.totalAmount = state.items.reduce((total, item) => total + (item.totalPrice * item.quantity), 0);
      }
    },
  },
});

export const {
  addToCart,
  removeFromCart,
  updateQuantity,
  clearCart,
  toggleCart,
  openCart,
  closeCart,
  updateCartItem,
} = cartSlice.actions;

export default cartSlice.reducer;
